import { useEffect, useState } from 'react';
import { MessageSquare, Trash2, Loader2, User, Sparkles, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminChat() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/chat/sessions');
      setSessions(r.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openSession = async (s) => {
    setSelected(s);
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/admin/chat/sessions/${s.id}/messages`);
      setMessages(r.data);
    } catch { toast.error('Erreur lors du chargement des messages'); }
    finally { setLoadingMsgs(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await api.delete(`/admin/chat/sessions/${id}`);
      setSessions(s => s.filter(x => x.id !== id));
      if (selected?.id === id) { setSelected(null); setMessages([]); }
      toast.success('Conversation supprimée');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="h2 text-anthracite-950">Conversations Assistant IA</h1>
          <p className="text-muted-foreground mt-1">{sessions.length} conversation{sessions.length > 1 ? 's' : ''} avec Beaulon Assistant</p>
        </div>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2 text-xs text-amber-900">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-700" />
        <span>Ces conversations sont stockées pour audit/qualité. Pensez à respecter le RGPD : ne diffusez pas le contenu sans le consentement du visiteur.</span>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-700" /></div>
      ) : sessions.length === 0 ? (
        <div className="card-base p-12 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-anthracite-300" />
          <p className="mt-3 text-muted-foreground">Aucune conversation pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-chat-sessions-list">
          {sessions.map(s => (
            <article key={s.id} className="card-base p-4">
              <div className="flex items-start justify-between gap-3">
                <button type="button" onClick={() => openSession(s)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <MessageSquare className="h-4 w-4 text-brand-700" />
                    <p className="font-heading font-semibold text-anthracite-950 truncate">{s.title || 'Conversation'}</p>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-brand-50 text-brand-700">{s.message_count || 0} msg</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" /> {new Date(s.updated_at).toLocaleString('fr-FR')}
                  </p>
                </button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openSession(s)}>Voir</Button>
                  <Button variant="outline" size="sm" onClick={() => remove(s.id)} className="text-destructive border-destructive/30 hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setMessages([]); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="truncate">{selected?.title || 'Conversation'}</DialogTitle>
          </DialogHeader>
          {loadingMsgs ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-brand-700" /></div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {messages.map(m => (
                <div key={m.id} className={`flex items-start gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
                    m.role === 'user' ? 'bg-anthracite-200 text-anthracite-700' : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white'
                  }`}>
                    {m.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user' ? 'bg-brand-500 text-white' : 'bg-anthracite-100 text-anthracite-900 border'
                  }`}>
                    {m.content}
                    <p className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-brand-100' : 'text-muted-foreground'}`}>
                      {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
